import { useState } from "react";
import { useMyMemberships } from "@/hooks/useMyMemberships";
import { useWorkspace } from "@/hooks/useWorkspace";
import { supabase } from "@/integrations/supabase/client";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Skeleton } from "@/components/ui/skeleton";
import { LogOut, Users } from "lucide-react";
import { toast } from "sonner";

const ROLE_LABEL = {
  owner_admin: "Owner / admin",
  editor: "Editor",
  viewer: "Viewer",
} as const;

/**
 * Lists workspaces the user is a member of (invited in by someone else).
 * Lets them leave gracefully — their content stays with the workspace,
 * their access ends immediately, and they can be re-invited later.
 */
export const MembershipsCard = () => {
  const { memberships, loading, refresh } = useMyMemberships();
  const { setActive, workspaces, refresh: refreshWs } = useWorkspace();
  const [busyId, setBusyId] = useState<string | null>(null);

  if (loading) {
    return (
      <Card className="shadow-soft">
        <CardHeader><CardTitle>Workspaces I'm a member of</CardTitle></CardHeader>
        <CardContent><Skeleton className="h-12 w-full" /></CardContent>
      </Card>
    );
  }

  if (memberships.length === 0) return null;

  const leave = async (id: string, label: string | null) => {
    if (!confirm(`Leave "${label ?? "this workspace"}"? Your access ends immediately. Your content stays with the workspace — you can be re-invited later.`)) return;
    setBusyId(id);
    const { error } = await supabase
      .from("workspace_members")
      .delete()
      .eq("id", id);
    setBusyId(null);
    if (error) return toast.error(error.message);
    toast.success("You've left the workspace");
    const primary = workspaces.find((w) => w.is_primary);
    if (primary) setActive(primary.id);
    await Promise.all([refresh(), refreshWs()]);
  };

  return (
    <Card className="shadow-soft">
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <Users className="h-5 w-5 text-primary" /> Workspaces I'm a member of
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-2">
        <p className="text-sm text-muted-foreground">
          Workspaces you've been invited to, plus any you previously owned and transferred. You can leave at any time — your access ends immediately.
        </p>
        <ul className="divide-y divide-border rounded-md border border-border">
          {memberships.map((m) => (
            <li key={m.id} className="flex flex-wrap items-center justify-between gap-3 px-3 py-2">
              <div className="min-w-0">
                <div className="flex items-center gap-2">
                  <span className="truncate text-sm font-medium text-foreground">
                    {m.workspace_label ?? "Workspace"}
                  </span>
                  <Badge variant="secondary" className="text-[10px]">{ROLE_LABEL[m.role]}</Badge>
                </div>
                <p className="text-xs text-muted-foreground">
                  {m.accepted_at
                    ? `Joined ${new Date(m.accepted_at).toLocaleDateString()}`
                    : `Invited ${new Date(m.invited_at).toLocaleDateString()}`}
                </p>
              </div>
              <Button
                variant="outline"
                size="sm"
                onClick={() => leave(m.id, m.workspace_label)}
                disabled={busyId === m.id}
              >
                <LogOut className="h-4 w-4" /> Leave
              </Button>
            </li>
          ))}
        </ul>
      </CardContent>
    </Card>
  );
};
